import { Component, OnInit, AfterViewInit, ChangeDetectorRef, ViewChild, OnDestroy } from '@angular/core';
import { Validators, UntypedFormBuilder, FormControl, ValidationErrors } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { UploadService } from 'src/app/_services/upload.service';
import { Party, PartyType } from 'src/app/_models/party';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/app/_services/language.service';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { UtilsService } from 'src/app/_shared/UtilsService';
import { AdditionalPartyComponent } from 'src/app/_shared/component/additional-party/additional-party.component';
import { checkTotalFileSizeExceeded } from 'src/app/_shared/file-validation.utils';
import { RecaptchaEnterpriseService } from 'src/app/_services/recaptcha-enterprise.service';

@Component({
  selector: 'app-request-mediation',
  standalone: false,
  templateUrl: './request-mediation.component.html',
  styleUrls: ['./request-mediation.component.scss']
})
export class RequestMediationComponent implements OnInit, OnDestroy {
  @ViewChild('additionalPartyForm', {static: false}) private additionalPartyForm: AdditionalPartyComponent;
  siteKey = environment.recaptchaSiteKey;
  AMC_LANGUAGE_COOKIE = 'amc_lang'; // WIPO language standard cookie name
  currentLanguage = 'en';
  requestingParty: Party;
  respondingParty: Party;
  additionalParties: Party[] = [];
  editedParty = new Party();

  editAdd = false;
  submitted = false;
  isROKMCST = false;

 

  blocked = false;
  isOpen = false;

  agreementFilename: string;
  paymentFilename: string;
  attachmentFilename: string;
  pageTitle: string;
  pageDetails: boolean = false;
  collaboration = 'NONE';

  translateSubscription: Subscription;
  invalidEmails = false;

  isTotalSizeExceeded = false;
  validRepresentedByComplainant = true;
  validRepresentedByRespondent = true;
  optionsCountries: { label: string; value: string }[] = [];

  requestForm = this.fb.group({
    nameComplainant: ['', [Validators.required, Validators.maxLength(255)]],
    addressComplainant: ['', [Validators.required, Validators.maxLength(255)]],
    countryComplainantCode: ['', Validators.required],
    telephoneComplainant: ['', Validators.maxLength(255)],
    emailComplainant: ['', [Validators.required, this.utilsService.validateEmail, Validators.maxLength(255)]],
    nameRespondent: ['', [Validators.required, Validators.maxLength(255)]],
    addressRespondent: ['', [Validators.required, Validators.maxLength(255)]],
    countryRespondentCode: ['', Validators.required],
    telephoneRespondent: ['', Validators.maxLength(255)],
    emailRespondent: ['', [Validators.required, this.utilsService.validateEmail, Validators.maxLength(255)]],
    representedByRespondent: ['', Validators.maxLength(255)],
    addressRepresentativeRespondent: ['', Validators.maxLength(255)],
    countryRespondentRepByCode: [''],
    telephoneRepresentativeRespondent: ['', Validators.maxLength(255)],
    emailRepresentativeRespondent: ['', [this.utilsService.validateEmail, Validators.maxLength(255)]],
    representedByComplainant: ['', Validators.maxLength(255)],
    addressRepresentativeComplainant: ['', Validators.maxLength(255)],
    countryComplainantRepByCode: [''],
    telephoneRepresentativeComplainant: ['', Validators.maxLength(255)],
    emailRepresentativeComplainant: ['', [this.utilsService.validateEmail, Validators.maxLength(255)]]
  });

  requestOtherForm = this.fb.group({
    disputeDescription: ['', [Validators.required, this.utilsService.customWordCountValidator, Validators.maxLength(18000)]],
    signature: ['', [Validators.required, Validators.maxLength(255)]],
    agreement: ['', Validators.required],
    payment: null,
    attachments: null
  });

 
  constructor(private cdr: ChangeDetectorRef, private utilsService: UtilsService,
    private fb: UntypedFormBuilder,
    private uploadService: UploadService,
    private activatedroute: ActivatedRoute, private translate: TranslateService, public languageService: LanguageService, private cookieService: CookieService, private recaptchaService: RecaptchaEnterpriseService) { }

  ngOnInit() {
    this.recaptchaService.load();
    this.activatedroute.data.subscribe(data => {
      if(data.collaboration) {  this.collaboration = data.collaboration; }
    })

    this.optionsCountries = this.utilsService.loadCountryOption();
    this.translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.currentLanguage = this.translate.currentLang;
      this.optionsCountries = this.utilsService.loadCountryOption();
    });
  }



  onAttachmentChange(files: File[]) {
    if (files.length > 0) {
      this.requestOtherForm.get('attachments').setValue(files);
      this.recalculateTotalSize();
    }
  }

  onAgreementChange(files: File[]) {
    if (files.length > 0) {
      this.requestOtherForm.get('agreement').setValue(files);
      this.recalculateTotalSize();
    }
  }

  onPaymentChange(files: File[]) {
    if (files.length > 0) {
      this.requestOtherForm.get('payment').setValue(files);
      this.recalculateTotalSize();
    }
  }

  private recalculateTotalSize() {
    this.isTotalSizeExceeded = checkTotalFileSizeExceeded(
      this.requestOtherForm.get('agreement')?.value,
      this.requestOtherForm.get('payment')?.value,
      this.requestOtherForm.get('attachments')?.value
    );
  }

  get f() { return this.requestForm.controls; }

  get g() { return this.requestOtherForm.controls; }

  customCheckValidation(): boolean {
    const complainantRepHasData = !!(this.requestForm.value['addressRepresentativeComplainant'] || this.requestForm.value['countryComplainantRepByCode'] || this.requestForm.value['telephoneRepresentativeComplainant'] || this.requestForm.value['emailRepresentativeComplainant']);
    this.validRepresentedByComplainant = !(complainantRepHasData && !this.requestForm.value['representedByComplainant']);

    const respondentRepHasData = !!(this.requestForm.value['addressRepresentativeRespondent'] || this.requestForm.value['countryRespondentRepByCode'] || this.requestForm.value['telephoneRepresentativeRespondent'] || this.requestForm.value['emailRepresentativeRespondent']);
    this.validRepresentedByRespondent = !(respondentRepHasData && !this.requestForm.value['representedByRespondent']);

    return this.validRepresentedByComplainant && this.validRepresentedByRespondent;
  }
 
  async onSubmit() {
    this.submitted = true;
    if (this.translate.currentLang) {
      this.currentLanguage = this.translate.currentLang
    } else {
      this.currentLanguage = 'en'
    }
    const isCustomValidationValid = this.customCheckValidation();
    if (this.requestForm.invalid || this.requestOtherForm.invalid || this.isTotalSizeExceeded || !isCustomValidationValid) {
      return;
    }
    if(!this.additionalPartyForm.validate()) { return; } // For additional parties validation 
    this.blocked = true;

    try {
      const token = await this.recaptchaService.executeScore('submit_mediation');
      await this.submitForm(token);
    } catch (error) {
      console.error('reCAPTCHA execution failed', error);
      this.blocked = false;
    }
  }

  private async submitForm(recaptchaToken: string) {
    let myFormData = new FormData();

    Object.keys(this.requestForm.controls).forEach((key) => {
      if (this.requestForm.value[key] === null) {
        myFormData.append(key, '');
      } else {
        myFormData.append(key, this.requestForm.value[key]);
      }
    });

    Object.keys(this.requestOtherForm.controls).forEach((key) => {
      if (this.requestOtherForm.value[key] === null) {
        myFormData.append(key, '');
      } else {
        const value = this.requestOtherForm.value[key];
        if (Array.isArray(value) && value.every(v => v instanceof File)) {
            value.forEach(f => myFormData.append(key, f));
        } else {
            myFormData.append(key, value);
        }
      }
    });

    myFormData.append('g_recaptcha_response', recaptchaToken);
    this.currentLanguage = this.cookieService.get(this.AMC_LANGUAGE_COOKIE);
    myFormData.append('collaboration', this.collaboration);
    myFormData.append('language', this.currentLanguage);
    myFormData.append('additionalParties', JSON.stringify(this.additionalParties));
    myFormData.append('countryComplainant', await this.utilsService.getCountryByCode(this.requestForm.value['countryComplainantCode']) || '');
    myFormData.append('countryRespondent', await this.utilsService.getCountryByCode(this.requestForm.value['countryRespondentCode']) || '');
    myFormData.append('countryComplainantRepBy', await this.utilsService.getCountryByCode(this.requestForm.value['countryComplainantRepByCode']) || '');
    myFormData.append('countryRespondentRepBy', await this.utilsService.getCountryByCode(this.requestForm.value['countryRespondentRepByCode']) || '');


    this.uploadService.submitMediationForm(myFormData).subscribe(
      response => {
        if (response instanceof HttpResponse) {
          if (response.status === 201) {
            this.blocked = false;
            this.isOpen = true;
          } else {
            this.blocked = false;
          }
        }
      }, err => {
        //  alert("Error caught at Subscriber " + err)
        this.blocked = false;
      }, () => { this.blocked = false; });
  }

  ngOnDestroy() {
    this.recaptchaService.unload();
    this.translateSubscription?.unsubscribe();
  }

  removeEle() {
    let elements = document.getElementsByClassName('b-input__multi-select--badge ng-star-inserted');
    while (elements.length > 0) {
      elements[0].parentNode.removeChild(elements[0]);
    }
  }

  closeDialogbox() {
    this.isOpen = false;
    this.submitted = false;
    this.removeEle();
    this.additionalParties = [];
    this.requestForm.reset();
    this.requestOtherForm.reset();
    this.isTotalSizeExceeded = false;
  }

}
